
import { defineComponent, onMounted, onBeforeUnmount, ref, computed, PropType, nextTick } from 'vue';
import BaseWysiwygEditor from './BaseWysiwygEditor.vue';
import { Translation } from '@/types';
import { Variable } from '../template-builder/utils/variables-list';

export default defineComponent({
  name: 'LazyWysiwygEditor',
  
  components: {
    BaseWysiwygEditor,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    translation: {
      type: Object as PropType<Translation>,
      required: false,
      default: null,
    },

    variablesList: {
      type: Array as PropType<Variable[]>,
      required: false,
      default: () => [],
    },

    contentClass: {
      type: String,
      required: false,
      default: '',
    },

    contentStylesheetHref: {
      type: String,
      required: false,
      default: '',
    },

    // Option pour forcer le chargement immédiat (utile pour debug)
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Mode de chargement: 'auto' (intersection), 'click' (clic uniquement), 'immediate' (immédiat)
    loadMode: {
      type: String,
      required: false,
      default: 'auto', // Par défaut, chargement au scroll
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },

    // Seuil de visibilité pour déclencher le chargement (0.1 = 10% visible)
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },
  },

  emits: {
    'update:modelValue': String,
    'activate-group': String,
    'deactivate-group': String,
  },

  setup(props) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    // Contenu tronqué pour l'aperçu
    const truncatedContent = computed(() => {
      if (!props.modelValue) return '';
      const textContent = props.modelValue.replace(/<[^>]*>/g, '');
      return textContent.length > 100 ? `${textContent.substring(0, 100)}...` : textContent;
    });

    const loadEditor = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        // Nettoyer l'observer une fois chargé
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadEditor();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          // Ajouter une marge pour commencer le chargement un peu avant que l'élément soit visible
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadEditor();
      } else if (props.loadMode === 'auto') {
        // Utiliser nextTick pour s'assurer que le DOM est prêt
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
      // Si loadMode === 'click', on ne fait rien, le chargement se fera au clic
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    // Référence vers le composant chargé
    const baseComponentRef = ref();

    // Méthode focus qui redirige vers le composant chargé
    const focus = () => {
      if (isLoaded.value && baseComponentRef.value && baseComponentRef.value.focus) {
        baseComponentRef.value.focus();
      } else if (!isLoaded.value) {
        // Si pas encore chargé, charger d'abord puis focus
        loadEditor();
        nextTick(() => {
          if (baseComponentRef.value && baseComponentRef.value.focus) {
            baseComponentRef.value.focus();
          }
        });
      }
    };

    // Méthodes pour contrôler l'Intersection Observer pendant le scroll
    const pauseObserver = () => {
      if (observer) {
        observer.disconnect();
      }
    };

    const resumeObserver = () => {
      if (!isLoaded.value && containerRef.value) {
        setupIntersectionObserver();
      }
    };

    return {
      containerRef,
      isLoaded,
      truncatedContent,
      loadEditor,
      baseComponentRef,
      focus, // Exposer focus via return
      pauseObserver, // Exposer pour contrôler l'observer
      resumeObserver, // Exposer pour contrôler l'observer
    };
  },
});
